/* Video product cards inside featured-collection (video mode).
   Compact layout (<992px, 2 per row) or any touch device: every card plays
   while it is on screen. Wide desktop: the most visible card autoplays, then
   hover takes over (one at a time).

   NOTE: <featured-collection-list> is already a custom element defined by the
   theme, so we attach plain controllers over [data-video-cards] groups instead
   of using customElements. */

(function () {
  var COMPACT = "(max-width: 991px)";

  class VideoCardController {
    constructor(group) {
      this.group = group;
      this.canHover =
        window.matchMedia && window.matchMedia("(hover: hover)").matches;
      this.ratios = new Map();
      this.interacted = false;
      this.evaluateMode();

      group.addEventListener("mouseover", (e) => {
        if (this.playAll) return;
        const card = this.cardFrom(e.target);
        if (!card) return;
        this.interacted = true;
        this.playOnly(card);
      });
      group.addEventListener("mouseout", (e) => {
        if (this.playAll) return;
        const card = this.cardFrom(e.target);
        if (!card) return;
        if (e.relatedTarget && card.contains(e.relatedTarget)) return;
        this.pause(card);
      });

      this.setupObserver();
      // Swiper adds cloned slides after it initialises — pick them up too.
      setTimeout(() => this.setupObserver(), 600);

      // Cards can be injected late (e.g. recently-viewed loads over AJAX).
      this.mo = new MutationObserver(() => {
        clearTimeout(this._moTimer);
        this._moTimer = setTimeout(() => this.setupObserver(), 150);
      });
      this.mo.observe(group, { childList: true });

      // On a slider the active slide changes on swipe (Swiper animates the
      // wrapper transform) — keep only the active slide playing.
      group.addEventListener("transitionend", () => {
        if (this.playAll && this.isSlider()) this.playActiveSlide();
      });

      let resizeTimer;
      window.addEventListener("resize", () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const was = this.playAll;
          this.evaluateMode();
          if (was !== this.playAll) {
            this.interacted = false;
            this.ratios.clear();
          }
          this.setupObserver();
        }, 250);
      });
    }

    evaluateMode() {
      this.playAll =
        !this.canHover ||
        (window.matchMedia && window.matchMedia(COMPACT).matches);
    }

    cards() {
      return this.group.querySelectorAll(".video-card");
    }

    cardFrom(node) {
      if (!node || !node.closest) return null;
      const card = node.closest(".video-card");
      return card && this.group.contains(card) ? card : null;
    }

    videoOf(card) {
      return card ? card.querySelector("video") : null;
    }

    play(card) {
      const v = this.videoOf(card);
      if (!v) return;
      card.classList.add("is-playing");
      v.muted = true;
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }

    pause(card) {
      const v = this.videoOf(card);
      if (!v) return;
      card.classList.remove("is-playing");
      try {
        v.pause();
      } catch (e) {}
    }

    playOnly(card) {
      this.cards().forEach((c) => {
        if (c !== card) this.pause(c);
      });
      this.play(card);
    }

    setupObserver() {
      if (this.io) this.io.disconnect();
      const options = this.playAll
        ? { threshold: 0, rootMargin: "-10% 0px -10% 0px" }
        : { threshold: [0, 0.25, 0.5, 0.75, 1] };

      this.io = new IntersectionObserver((entries) => {
        if (this.playAll) {
          if (this.isSlider()) {
            // Slider on mobile: only the active slide plays.
            this.playActiveSlide();
          } else {
            // Grid: every card plays while it overlaps the central band.
            entries.forEach((e) => {
              if (e.isIntersecting) this.play(e.target);
              else this.pause(e.target);
            });
          }
        } else {
          // Wide desktop: autoplay the most visible card until first hover.
          if (this.interacted) {
            this.io.disconnect();
            this.io = null;
            return;
          }
          entries.forEach((e) => this.ratios.set(e.target, e.intersectionRatio));
          let best = null;
          let bestRatio = 0;
          this.ratios.forEach((ratio, card) => {
            if (card.isConnected && ratio > bestRatio) {
              bestRatio = ratio;
              best = card;
            }
          });
          if (best && bestRatio >= 0.3) this.playOnly(best);
        }
      }, options);

      this.cards().forEach((c) => this.io.observe(c));

      // Don't wait for the observer: start the right card right away
      // (fixes first-card not autoplaying on desktop / late AJAX cards).
      if (this.playAll) {
        if (this.isSlider()) this.playActiveSlide();
      } else {
        this.ensureInitialPlay();
      }
    }

    isSlider() {
      return (
        this.group.classList.contains("swiper-wrapper") ||
        !!this.group.querySelector(".swiper-slide")
      );
    }

    playActiveSlide() {
      const active = this.group.querySelector(".swiper-slide-active");
      this.cards().forEach((c) => {
        if (c === active) this.play(c);
        else this.pause(c);
      });
    }

    ensureInitialPlay() {
      if (this.playAll || this.interacted) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let best = null;
      let bestArea = 0;
      this.cards().forEach((c) => {
        if (!c.isConnected || !this.videoOf(c)) return;
        const r = c.getBoundingClientRect();
        const w = Math.max(0, Math.min(r.right, vw) - Math.max(r.left, 0));
        const h = Math.max(0, Math.min(r.bottom, vh) - Math.max(r.top, 0));
        const area = w * h;
        if (area > bestArea) {
          bestArea = area;
          best = c;
        }
      });
      if (best) this.playOnly(best);
    }
  }

  function init() {
    document
      .querySelectorAll("featured-collection-list[data-video-cards]")
      .forEach((group) => {
        if (group.dataset.videoInit) return;
        group.dataset.videoInit = "1";
        new VideoCardController(group);
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  window.addEventListener("load", init);
  // Re-init after Swiper builds clones and when edited in the theme editor
  document.addEventListener("shopify:section:load", init);
})();
